import { useEffect } from 'react';
import { Button } from '@cuito/ui';
import { AMOUNT_UNAVAILABLE_TEXT, formatServerYuan } from '../../lib/schemeForm';
import type { OrderQueueItem } from '../api/orders';

/**
 * 新订单浮层 + 提示音（§6.2）。
 *
 * 音色：Web Audio 双音 **880Hz 120ms → 静音 60ms → 1174Hz 160ms**，
 * gain 包络 5ms 淡入 / 40ms 淡出（避免爆音）。
 *
 * ⚠️ **`AudioContext` 必须在首次用户交互之后创建**：浏览器自动播放策略会让
 * 「页面一加载就 new AudioContext + start」静默失败（`state === 'suspended'`），
 * 而收银员看到的是「有单没响」。这里把「创建」收进 `unlockNewOrderSound()`，
 * 由接单面板挂在第一次 `keydown`/`pointerdown` 上；**没解锁之前 `playNewOrderChime`
 * 直接 return** —— 于是「没交互就没声音」是一条可断言的契约，而不是靠浏览器兜着。
 *
 * ⚠️ **提示音开关（§6.2 的 `newOrderSound`）只落本地**：`04-api-contract.md` §7.7 的
 * `PUT /locations/:id/settings/:key` 白名单里没有这个键（写它必 400
 * `SETTING_KEY_INVALID`），所以开关值走 `lib/prefs` 的 `cuito-cashier-sound`，
 * 不假装写回服务端成功、也不吞那个 400（见 `prefs.ts` 与计划附录 C）。
 */
export const NEW_ORDER_TONES = [
  { frequency: 880, duration: 0.12 },
  { frequency: 1174, duration: 0.16 },
] as const;

/** 静音 60ms（两音之间的间隔） */
export const NEW_ORDER_GAP_SECONDS = 0.06;

/** 默认音量 0.7（§6.2；地点设置 `newOrderVolume` 不在契约 §7.7 的白名单里，见冲突清单第 3 条） */
export const DEFAULT_VOLUME = 0.7;

let audioContext: AudioContext | null = null;

/** 在**首次用户交互**的处理函数里调用：创建 AudioContext 并留给后续播放 */
export function unlockNewOrderSound(): void {
  if (audioContext !== null) return;
  const Ctor = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
  if (Ctor === undefined) return; // 无 Web Audio 的环境（jsdom 未打桩）安静跳过，不影响接单
  try {
    audioContext = new Ctor();
  } catch {
    audioContext = null;
  }
}

/** 提示音是否已就绪（面板用它决定「本 document 已解锁，不必再点一次」） */
export function newOrderSoundReady(): boolean {
  return audioContext !== null;
}

/**
 * 丢掉已建好的 `AudioContext`，让下一次「首次用户交互」重新走解锁路径。
 *
 * 用途**只有测试**：`audioContext` 是模块级单例，同一个测试文件里前面的用例一解锁，
 * 后面的用例挂载时 `newOrderSoundReady()` 就已经是 `true` —— 于是「首次交互才解锁」
 * 这条契约（以及随之出现的「提示音已就绪」提示条）会**静默失效**，用例之间靠执行顺序
 * 互相污染（既有用例曾用「增量断言」绕开它，见 `CashierBoardPage.spec.tsx`）。
 * 显式重置比绕开更可靠：泄漏被消除，断言可以写成绝对值。
 */
export function resetNewOrderSound(): void {
  const ctx = audioContext;
  audioContext = null;
  if (ctx === null) return;
  try {
    void ctx.close();
  } catch {
    // 关闭失败不影响「已重置」这件事；测试里用的是桩，真实浏览器里也没人调用本函数
  }
}

export async function playNewOrderChime(volume: number = DEFAULT_VOLUME): Promise<void> {
  if (audioContext === null) return;
  const ctx = audioContext;
  if (ctx.state === 'suspended') await ctx.resume();

  let start = ctx.currentTime;
  for (const tone of NEW_ORDER_TONES) {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.frequency.value = tone.frequency;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(volume, start + 0.005);
    gain.gain.linearRampToValueAtTime(0, start + tone.duration - 0.04);
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start(start);
    oscillator.stop(start + tone.duration);
    start += tone.duration + NEW_ORDER_GAP_SECONDS;
  }
}

interface Props {
  order: OrderQueueItem;
  onAccept: () => void;
  onLater: () => void;
}

/** 浮层停留 15 秒后自动滑出（§6.2） */
export const TOAST_DWELL_MS = 15_000;

export default function NewOrderToast({ order, onAccept, onLater }: Props) {
  useEffect(() => {
    const timer = setTimeout(onLater, TOAST_DWELL_MS);
    return () => clearTimeout(timer);
  }, [onLater]);

  return (
    <div
      data-testid="new-order-toast"
      role="alert"
      aria-live="assertive"
      className="fixed right-4 top-[calc(1rem+env(safe-area-inset-top))] z-[1000] flex w-80 flex-col gap-2 rounded-lg border-2 border-black bg-white p-3 shadow-lg"
    >
      <p className="text-base font-bold">
        新订单 · {order.itemCount} 件 · {formatServerYuan(order.totalAmount) ?? AMOUNT_UNAVAILABLE_TEXT}
      </p>
      <p className="text-sm text-gray-500">短码 {order.shortCode}</p>
      <div className="flex items-center justify-between">
        <Button onClick={onAccept}>接单</Button>
        <button type="button" onClick={onLater} className="text-sm text-gray-500">
          稍后
        </button>
      </div>
    </div>
  );
}
